import { ChangeEventHandler, FC, useMemo } from 'react';

import clsx from 'clsx';
import { isNonNullish, isNullish } from 'remeda';
import useSWR from 'swr';

import TextField from '@mui/material/TextField';

import { SPECIAL_REWARDS } from './constants';

import css from './StoreProductRewardPicker.module.css';

import { ticketService } from 'v2/api/ticketService';
import { MarkedAutocomplete } from 'v2/components/autocomplete/markedAutocomplete/MarkedAutocomplete';
import { Select } from 'v2/components/select/Select';
import { StoreProductType, TicketType } from 'v2/constants';
import { SelectOption } from 'v2/types';
import { enumToSelect } from 'v2/utils';

interface StoreProductRewardPickerProps {
  type: StoreProductType;
  onTypeChange: (value: StoreProductType) => void;
  value: string;
  onValueChange: (value: string) => void;
  casinoLink?: string;
  casinoName?: string;
  promoCodes: string[];
  onFormikChange: ChangeEventHandler<HTMLInputElement>;
  onPromoCodesChange: (values: string[]) => void;
}

export const StoreProductRewardPicker: FC<StoreProductRewardPickerProps> = ({
  type,
  onTypeChange,
  value,
  onValueChange,
  casinoLink,
  casinoName,
  promoCodes,
  onFormikChange,
  onPromoCodesChange,
}) => {
  const { data: tickets } = useSWR(type === StoreProductType.GOLDEN_TICKET && 'getTickets', ticketService.getTickets);

  const filteredTickets = useMemo(() => {
    if (isNullish(tickets)) return [];

    if (type === StoreProductType.GOLDEN_TICKET) {
      return tickets.filter((ticket) => ticket.type === TicketType.GOLDEN);
    }

    return tickets;
  }, [tickets, type]);

  const ticketsSelectOptions: SelectOption[] = useMemo(() => {
    if (isNullish(filteredTickets)) return [];

    return filteredTickets.map((ticket) => ({ id: ticket.id, label: ticket.title, value: ticket.id }));
  }, [filteredTickets]);

  const handleTextFieldChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onValueChange(e.target.value);
  };

  return (
    <div className={clsx('form-group', css.storeProductRewardPicker)}>
      <Select
        value={type}
        options={enumToSelect(StoreProductType)}
        label="Reward Type"
        onChange={onTypeChange}
        required
      />

      {isNonNullish(tickets) && (
        <Select value={value} options={ticketsSelectOptions} label="Reward Value" onChange={onValueChange} required />
      )}

      {type === StoreProductType.PROMOCODE && (
        <div className={css.storeProductRewardPickerPromo}>
          <TextField
            name="casinoLink"
            value={casinoLink ?? ''}
            onChange={onFormikChange}
            label="Casino Link"
            required
            fullWidth
          />

          <TextField
            name="casinoName"
            value={casinoName ?? ''}
            onChange={onFormikChange}
            label="Casino Name"
            required
            fullWidth
          />

          <MarkedAutocomplete
            label="Promo Code's Names"
            value={promoCodes}
            options={promoCodes}
            onChange={onPromoCodesChange}
            required
          />
        </div>
      )}

      {!SPECIAL_REWARDS.includes(type) && (
        <TextField
          name="value"
          value={value}
          onChange={handleTextFieldChange}
          label="Reward Value"
          required
          fullWidth
        />
      )}
    </div>
  );
};
